// DataService.js - Service pour la facturation mensuelle
import api from '../../utils/ApiConfig';

/**
 * Récupère le résumé mensuel des commandes à facturer
 * @param {number} year - Année
 * @param {number} month - Mois (optionnel)
 * @param {string} clientType - Type de client (optionnel)
 */
export const getMonthlySummary = async (year, month = null, clientType = null) => {
  try {
    const params = { year };
    if (month) params.month = month;
    if (clientType) params.client_type = clientType;

    const queryString = Object.entries(params)
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
      .join('&');

    const response = await api.get(`/bo/billing/monthly-summary?${queryString}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching monthly summary:', error);
    throw error;
  }
};

/**
 * Rend la facture telle qu'elle sortirait, sans rien écrire côté serveur.
 *
 * Mêmes paramètres que generateInvoice, et le serveur sélectionne les mêmes
 * commandes : l'aperçu ne vaut que s'il porte exactement sur ce qui sera émis.
 *
 * Le PDF revient en blob plutôt que par une URL : une <iframe> ne peut pas
 * porter l'en-tête d'authentification, et un objet blob évite d'avoir à ouvrir
 * une URL signée pour un document qui n'existe que le temps d'un regard.
 *
 * @returns {{url: string, manutentionsAConfirmer: number}} url à révoquer par
 *          l'appelant avec URL.revokeObjectURL une fois la modale fermée.
 */
export const previewInvoice = async (clientId, year, month, orderIds = null) => {
  const data = { client_id: clientId, year, month };
  if (orderIds && orderIds.length > 0) {
    data.order_ids = orderIds;
  }

  const response = await api.post('/bo/billing/preview-invoice', data, {
    responseType: 'blob',
  });

  return {
    url: window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' })),
    // Les manutentions non confirmées valent 0 € et c'est ce 0 qui serait
    // facturé. L'aperçu ne bloque pas, mais il le signale.
    manutentionsAConfirmer: Number(response.headers['x-manutentions-a-confirmer'] || 0),
  };
};

/**
 * Génère une facture mensuelle pour un client
 * @param {number} clientId - ID du client
 * @param {number} year - Année
 * @param {number} month - Mois
 * @param {Array} orderIds - IDs des commandes (optionnel)
 */
export const generateInvoice = async (clientId, year, month, orderIds = null) => {
  try {
    const data = {
      client_id: clientId,
      year,
      month
    };
    if (orderIds && orderIds.length > 0) {
      data.order_ids = orderIds;
    }

    const response = await api.post('/bo/billing/generate-invoice', data);
    return response.data;
  } catch (error) {
    console.error('Error generating invoice:', error);
    throw error;
  }
};

/**
 * Envoie une facture par email au client
 * @param {string} numFacture - Numéro de facture
 * @param {number} clientId - ID du client
 * @param {string} customMessage - Message personnalisé (optionnel)
 */
export const sendInvoice = async (numFacture, clientId, customMessage = null) => {
  try {
    const data = {
      num_facture: numFacture,
      client_id: clientId
    };
    if (customMessage) {
      data.custom_message = customMessage;
    }

    const response = await api.post('/bo/billing/send-invoice', data);
    return response.data;
  } catch (error) {
    console.error('Error sending invoice:', error);
    throw error;
  }
};

/**
 * Met à jour le statut de paiement d'une facture
 * @param {string} numFacture - Numéro de facture
 * @param {number} statuFacture - Statut (1=non payé, 2=payé)
 */
export const updatePaymentStatus = async (numFacture, statuFacture) => {
  try {
    const response = await api.patch(`/bo/billing/update-payment-status/${numFacture}`, {
      statu_facture: statuFacture
    });
    return response.data;
  } catch (error) {
    console.error('Error updating payment status:', error);
    throw error;
  }
};

/**
 * Corrige la date de paiement d'une facture déjà marquée payée
 * @param {string} numFacture - Numéro de facture
 * @param {string} paidAt - Date de paiement (YYYY-MM-DD)
 */
export const updatePaymentDate = async (numFacture, paidAt) => {
  try {
    const response = await api.patch(`/bo/billing/update-payment-date/${numFacture}`, {
      paid_at: paidAt
    });
    return response.data;
  } catch (error) {
    console.error('Error updating payment date:', error);
    throw error;
  }
};

/**
 * Récupère la liste des factures avec filtres
 * @param {Object} filters - Filtres (statuFacture, clientId, year, month, search, page, nbpage)
 */
export const getInvoices = async (filters = {}) => {
  try {
    const params = {
      page: filters.page || 1,
      nbpage: filters.nbpage || 10
    };

    if (filters.statuFacture) params.statu_facture = filters.statuFacture;
    if (filters.clientId) params.client_id = filters.clientId;
    if (filters.year) params.year = filters.year;
    if (filters.month) params.month = filters.month;
    if (filters.search) params.search = filters.search;

    const queryString = Object.entries(params)
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
      .join('&');

    const response = await api.get(`/bo/billing/invoices?${queryString}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching invoices:', error);
    throw error;
  }
};

/**
 * Récupère le vieillissement des factures mensuelles impayées (clients en compte) :
 * détail par facture + totaux par tranche d'ancienneté, basés sur la vraie date
 * d'échéance (fin du mois suivant la livraison).
 */
export const getEncours = async () => {
  try {
    const response = await api.get('/bo/billing/encours');
    return response.data;
  } catch (error) {
    console.error('Error fetching encours:', error);
    throw error;
  }
};

/**
 * Récupère la liste des factures impayées avec calcul du retard
 * @param {Object} filters - Filtres (minDays, search, page, nbpage)
 */
export const getUnpaidInvoices = async (filters = {}) => {
  try {
    const params = {
      page: filters.page || 1,
      nbpage: filters.nbpage || 10,
      min_days: filters.minDays !== undefined ? filters.minDays : 7
    };

    if (filters.search) params.search = filters.search;

    const queryString = Object.entries(params)
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
      .join('&');

    const response = await api.get(`/bo/billing/unpaid-invoices?${queryString}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching unpaid invoices:', error);
    throw error;
  }
};

/**
 * Importe un relevé bancaire (CSV) pour rapprochement — traitement asynchrone,
 * un email de récap est envoyé une fois le traitement terminé.
 */
export const importBankStatement = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post('/bo/billing/rapprochement/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

/**
 * Liste des imports de relevés bancaires (les plus récents en premier)
 */
export const getBankStatementImports = async () => {
  const response = await api.get('/bo/billing/rapprochement/imports');
  return response.data;
};

/**
 * Transactions en attente de validation (proposées) et non rapprochées
 */
export const getPendingReconciliations = async () => {
  const response = await api.get('/bo/billing/rapprochement/pending');
  return response.data;
};

/**
 * Valide un rapprochement (facultativement avec un numéro de facture différent
 * de la proposition par défaut, en cas d'ambiguïté)
 */
export const validateReconciliation = async (transactionId, numFacture = null) => {
  const response = await api.post(`/bo/billing/rapprochement/transactions/${transactionId}/validate`, {
    num_facture: numFacture,
  });
  return response.data;
};

/**
 * Rejette une proposition de rapprochement
 */
export const rejectReconciliation = async (transactionId) => {
  const response = await api.post(`/bo/billing/rapprochement/transactions/${transactionId}/reject`);
  return response.data;
};

/**
 * Historique d'une facture : qui a fait quoi, et quand.
 *
 * Les libellés sont composés au serveur : `action` et `origine` sont des codes
 * internes, et les traduire ici aussi les ferait diverger au premier acte ajouté.
 */
export const getInvoiceHistorique = async (numFacture) => {
  const response = await api.get(`/bo/billing/invoices/${numFacture}/historique`);
  return response.data;
};
